import React from "react";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { LogicRules } from "./NewLogicModal";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import {
  getFieldOptionById,
  getFieldDisplayLabel,
  getChoiceLabelByIndex,
  getDisplayCategoryName,
  getOperatorDisplayText,
  capitalizeFirstLetter,
} from "../../utils/ruleFormatters";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  isMeasurementUnitParameter,
  formatHeightDisplay,
  convertWeightToDisplay,
} from "./measurementUnitHelpers";
import { formatDisplayDate } from "../../../../common/utils/date";

interface RulesPreviewProps {
  rules: LogicRules;
  bgColor?: string;
}

const RulesPreview = ({ rules, bgColor }: RulesPreviewProps) => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();
  const categoryOptions = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.categoryOptions
  );

  // Get unitType from authenticated user (1 = metric, 2 = imperial)
  const unitType = AuthUser.unitType;

  return (
    <div
      style={{
        ...styles.rulesWrapper(theme),
        backgroundColor: bgColor || theme.colorTokens.P[10],
      }}
    >
      <div style={styles.ruleContainerTitle(theme)}>Rule summary</div>
      {rules.list[0].conditions.length > 0 && (
        <div style={styles.ruleContainerSubtitle(theme)}>
          {`Condition(s) ${
            rules.list[0].conditions.length > 1 && rules.list[0].match
              ? capitalizeFirstLetter(rules.list[0].match)
              : ""
          }`}
        </div>
      )}
      {rules.list[0].conditions.length > 0 ? (
        <div style={styles.conditionContainer}>
          {rules.list[0].conditions.map((condition, condIndex) => (
            <div key={condIndex}>
              <div style={styles.conditionLabel(theme)}>
                {condition.object_name
                  ? `${condIndex + 1}. ${getDisplayCategoryName(
                      condition.object_name
                    )} ${
                      condition.fields.length > 1 && condition.match
                        ? `(${capitalizeFirstLetter(condition.match)})`
                        : ""
                    }`
                  : ""}
              </div>
              <ul style={styles.paramsList}>
                {condition.fields.map((f, fieldIndex: number) => {
                  const fieldOption = getFieldOptionById(
                    categoryOptions,
                    condition.object_name,
                    f.name
                  );
                  const fieldLabel = getFieldDisplayLabel(
                    categoryOptions,
                    condition.object_name,
                    f.name
                  );
                  let fieldUnit = fieldOption?.unit || "";

                  // Get the proper value display - either choice label or raw value with unit
                  let displayValue: string;

                  if (
                    fieldOption?.choices &&
                    fieldOption.choices.length > 0 &&
                    f.value !== null
                  ) {
                    // For biometric/lifestyle questions with choices, show the choice label
                    displayValue = getChoiceLabelByIndex(fieldOption, f.value);
                  } else if (
                    fieldOption?.type === "datepicker" &&
                    f.value !== null
                  ) {
                    // Date criteria (e.g. birth_date) — format via shared util
                    displayValue = formatDisplayDate(f.value);
                  } else {
                    // Handle measurement unit conversion
                    let valueToDisplay = f.value;

                    if (
                      isMeasurementUnitParameter(f.name) &&
                      f.value !== null
                    ) {
                      if (f.name === "height") {
                        // Format height display based on unitType
                        displayValue = formatHeightDisplay(
                          Number(f.value),
                          unitType
                        );
                        return (
                          <li
                            key={fieldIndex}
                            style={styles.conditionValue(theme)}
                          >
                            <span>{fieldLabel}</span>
                            {f.operator && (
                              <span
                                style={{
                                  color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                                }}
                              >{` ${getOperatorDisplayText(
                                f.operator
                              ).toLowerCase()} `}</span>
                            )}
                            <span>{displayValue}</span>
                          </li>
                        );
                      } else if (
                        f.name === "current_weight" ||
                        f.name === "target_weight"
                      ) {
                        if (unitType === 2) {
                          // Convert kg to lbs (rounded)
                          valueToDisplay = convertWeightToDisplay(
                            Number(f.value),
                            unitType
                          );
                          fieldUnit = "lbs";
                        } else {
                          // Metric - keep as kg with decimals
                          valueToDisplay = f.value;
                          fieldUnit = "kg";
                        }
                      }
                    }

                    // For numeric fields (like Pnoe Metrics), show value with unit
                    const shouldShowUnit =
                      fieldUnit && fieldUnit !== "high/low";
                    displayValue = shouldShowUnit
                      ? `${valueToDisplay}${fieldUnit !== "%" ? " " : ""}${fieldUnit}`
                      : valueToDisplay === 100
                      ? "High"
                      : valueToDisplay === 10
                      ? "Low"
                      : valueToDisplay?.toString() || "";
                  }

                  return (
                    <li key={fieldIndex} style={styles.conditionValue(theme)}>
                      <span>{fieldLabel}</span>
                      {f.operator && (
                        <span
                          style={{
                            // fontFamily: "var(--font-Regular)",
                            color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                          }}
                        >{` ${getOperatorDisplayText(
                          f.operator
                        ).toLowerCase()} `}</span>
                      )}
                      <span>{displayValue}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.noConditionText(theme)}>No conditions defined.</div>
      )}
    </div>
  );
};

export default RulesPreview;

const styles = {
  rulesWrapper: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    padding: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
  }),
  ruleContainerTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    paddingBottom: "16px",
  }),
  ruleContainerSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    paddingBottom: "8px",
  }),
  paramsList: {
    listStylePosition: "inside" as "inside",
  },
  conditionContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  conditionLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  conditionValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  noConditionText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
